import type { Expense, CategoryViewModel } from '../types';
import { convertCurrency } from '../../utils/currency';
import { CategoryService } from '../category';

export interface CategoryInsight {
  category: CategoryViewModel;
  totalSpent: number;
  percentage: number; // Of total spending
  transactionCount: number;
  averageAmount: number;
  comparisonAmount?: number; // vs previous period
  trend?: 'up' | 'down' | 'stable';
  trendPercentage?: number; // % change vs previous period
}

export class InsightsService {
  /**
   * Get category breakdown for a date range
   */
  static async getCategoryBreakdown(
    expenses: Expense[],
    startDate: string,
    endDate: string,
    defaultCurrency: string,
    userId: string,
    theme: 'light' | 'dark' = 'light'
  ): Promise<CategoryInsight[]> {
    // Filter expenses by date range
    // Use string comparison to avoid timezone issues
    const filteredExpenses = expenses.filter(e => {
      return e.date >= startDate && e.date <= endDate;
    });

    // Calculate total spending
    const totalSpending = filteredExpenses.reduce((sum, expense) => {
      return sum + convertCurrency(expense.amount, expense.currency, defaultCurrency);
    }, 0);

    // Group by category
    const categoryMap = new Map<string, {
      expenses: Expense[];
      totalSpent: number;
    }>();

    filteredExpenses.forEach(expense => {
      const categoryId = expense.categoryId;
      if (!categoryId) return;

      if (!categoryMap.has(categoryId)) {
        categoryMap.set(categoryId, { expenses: [], totalSpent: 0 });
      }

      const categoryData = categoryMap.get(categoryId)!;
      categoryData.expenses.push(expense);
      categoryData.totalSpent += convertCurrency(
        expense.amount,
        expense.currency,
        defaultCurrency
      );
    });

    // Load all categories in parallel for better performance
    const categoryIds = Array.from(categoryMap.keys());
    const categoryPromises = categoryIds.map(id =>
      CategoryService.getCategoryById(id, userId, theme)
    );
    const categories = await Promise.all(categoryPromises);

    // Build insights from loaded categories
    const insights: CategoryInsight[] = [];

    categories.forEach((category, index) => {
      if (!category) return;

      const categoryId = categoryIds[index];
      const data = categoryMap.get(categoryId)!;

      const percentage = totalSpending > 0 ? (data.totalSpent / totalSpending) * 100 : 0;
      const averageAmount = data.expenses.length > 0
        ? data.totalSpent / data.expenses.length
        : 0;

      insights.push({
        category,
        totalSpent: data.totalSpent,
        percentage,
        transactionCount: data.expenses.length,
        averageAmount,
      });
    });

    // Sort by total spent (highest first)
    insights.sort((a, b) => b.totalSpent - a.totalSpent);

    return insights;
  }

  /**
   * Compare two periods
   */
  static comparePeriods(
    current: CategoryInsight[],
    previous: CategoryInsight[]
  ): CategoryInsight[] {
    return current.map(currentInsight => {
      const previousInsight = previous.find(
        p => p.category.id === currentInsight.category.id
      );

      if (!previousInsight) {
        return {
          ...currentInsight,
          trend: 'stable' as const,
          trendPercentage: 0,
        };
      }

      const change = currentInsight.totalSpent - previousInsight.totalSpent;
      const changePercentage = previousInsight.totalSpent > 0
        ? (change / previousInsight.totalSpent) * 100
        : 0;

      let trend: 'up' | 'down' | 'stable';
      if (Math.abs(changePercentage) < 5) {
        trend = 'stable';
      } else if (change > 0) {
        trend = 'up';
      } else {
        trend = 'down';
      }

      return {
        ...currentInsight,
        comparisonAmount: previousInsight.totalSpent,
        trend,
        trendPercentage: Math.abs(changePercentage),
      };
    });
  }

  /**
   * Get top spending categories (limit N)
   */
  static getTopCategories(
    insights: CategoryInsight[],
    limit: number
  ): CategoryInsight[] {
    return insights.slice(0, limit);
  }

  /**
   * Helper function to format date as YYYY-MM-DD in local timezone
   * Avoids toISOString() which converts to UTC and can cause date shifts
   */
  private static formatLocalDate(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  /**
   * Get date range for current month
   */
  static getCurrentMonthRange(): { start: string; end: string } {
    const now = new Date();
    const start = new Date(now.getFullYear(), now.getMonth(), 1);
    const end = new Date(now.getFullYear(), now.getMonth() + 1, 0);

    return {
      start: this.formatLocalDate(start),
      end: this.formatLocalDate(end),
    };
  }

  /**
   * Get date range for previous month
   */
  static getPreviousMonthRange(): { start: string; end: string } {
    const now = new Date();
    const start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const end = new Date(now.getFullYear(), now.getMonth(), 0);

    return {
      start: this.formatLocalDate(start),
      end: this.formatLocalDate(end),
    };
  }

  /**
   * Format month name from date string
   * Uses string parsing to avoid timezone issues
   */
  static formatMonthYear(dateString: string): string {
    const [year, month] = dateString.split('-').map(Number);
    const date = new Date(year, month - 1, 1);
    return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  }

  /**
   * Get date range for a specific month and year
   */
  static getMonthRange(year: number, month: number): { start: string; end: string } {
    const start = new Date(year, month, 1);
    const end = new Date(year, month + 1, 0);

    return {
      start: this.formatLocalDate(start),
      end: this.formatLocalDate(end),
    };
  }

  /**
   * Get available months with expenses
   */
  static getAvailableMonths(expenses: Expense[]): { year: number; month: number }[] {
    const monthSet = new Set<string>();
    expenses.forEach(expense => {
      // Parse date string directly to avoid timezone issues
      const [year, month] = expense.date.split('-').map(Number);
      // Month in ISO string is 1-indexed, convert to 0-indexed for JavaScript
      const jsMonth = month - 1;
      const key = `${year}-${jsMonth}`;
      monthSet.add(key);
    });

    return Array.from(monthSet)
      .map(key => {
        const [year, month] = key.split('-').map(Number);
        return { year, month };
      })
      .sort((a, b) => {
        if (a.year !== b.year) return b.year - a.year;
        return b.month - a.month;
      });
  }
}
